import React from 'react';
import { motion } from 'framer-motion';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { useForm } from '../../hooks/useForm';
import { fadeInLeft, fadeInRight } from '../../utils/animation';
import { validateContactForm } from '../../utils/validation';
import { PERSONAL_INFO, SOCIAL_LINKS } from '../../data/constants';
import { ContactFormData } from '../../types';
import { IconMail, IconPhone, IconMapPin, IconGithub, IconLinkedin, IconArrowRight } from '../ui/Icons';
import './Contact.css';

const SOCIAL_ICON_MAP: Record<string, React.ReactNode> = {
  github:   <IconGithub   size={17} />,
  linkedin: <IconLinkedin size={17} />,
  email:    <IconMail     size={17} />,
};

export const Contact: React.FC = () => {
  const { ref, inView } = useIntersectionObserver({ threshold: 0.08, triggerOnce: true });
  const { values, errors, isSubmitting, handleChange, handleSubmit } = useForm<ContactFormData>({
    initialValues: { name: '', email: '', subject: '', message: '' },
    onSubmit: async (data) => {
      console.log('Contact form submitted:', data);
      alert("Thanks for your message - I'll be in touch shortly.");
    },
    validate: validateContactForm,
  });

  return (
    <section id="contact" className="contact section section--dark" ref={ref}>
      <div className="container">
        <div className="section__header">
          <span className="section__subtitle">GET IN TOUCH</span>
          <h2 className="section__title">Let's Work Together</h2>
          <p className="section__description">
            Open to senior IC and lead roles in AI/ML engineering, data platform engineering,
            or GenAI product teams. Remote-friendly.
          </p>
        </div>

        <div className="contact__layout">
          {/* Left: info */}
          <motion.div
            className="contact__info"
            variants={fadeInLeft}
            initial="initial"
            animate={inView ? 'animate' : 'initial'}
          >
            <h3 className="contact__info-heading">Contact Details</h3>

            <div className="contact__details">
              <a href={`mailto:${PERSONAL_INFO.email}`} className="contact__detail">
                <div className="contact__detail-icon"><IconMail size={17} /></div>
                <div>
                  <span className="contact__detail-label">Email</span>
                  <span className="contact__detail-value">{PERSONAL_INFO.email}</span>
                </div>
              </a>
              {PERSONAL_INFO.phone && (
                <div className="contact__detail">
                  <div className="contact__detail-icon"><IconPhone size={17} /></div>
                  <div>
                    <span className="contact__detail-label">Phone</span>
                    <span className="contact__detail-value">{PERSONAL_INFO.phone}</span>
                  </div>
                </div>
              )}
              <div className="contact__detail">
                <div className="contact__detail-icon"><IconMapPin size={17} /></div>
                <div>
                  <span className="contact__detail-label">Location</span>
                  <span className="contact__detail-value">{PERSONAL_INFO.location}</span>
                </div>
              </div>
            </div>

            <div className="contact__socials">
              <p className="contact__socials-label">Find me on</p>
              <div className="contact__social-row">
                {SOCIAL_LINKS.filter(l => SOCIAL_ICON_MAP[l.icon]).map(link => (
                  <motion.a
                    key={link.platform}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="contact__social"
                    aria-label={link.label}
                    whileHover={{ y: -3 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    {SOCIAL_ICON_MAP[link.icon]}
                    <span>{link.label}</span>
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>

          {/* Right: form */}
          <motion.form
            className="contact__form"
            variants={fadeInRight}
            initial="initial"
            animate={inView ? 'animate' : 'initial'}
            onSubmit={handleSubmit}
          >
            <div className="contact__form-row">
              <div className="contact__field">
                <label className="contact__label">Name</label>
                <input
                  type="text"
                  name="name"
                  placeholder="Your name"
                  value={values.name}
                  onChange={handleChange}
                  className={`contact__input ${errors.name ? 'contact__input--error' : ''}`}
                  required
                />
                {errors.name && <span className="contact__error">{errors.name}</span>}
              </div>
              <div className="contact__field">
                <label className="contact__label">Email</label>
                <input
                  type="email"
                  name="email"
                  placeholder="your@email.com"
                  value={values.email}
                  onChange={handleChange}
                  className={`contact__input ${errors.email ? 'contact__input--error' : ''}`}
                  required
                />
                {errors.email && <span className="contact__error">{errors.email}</span>}
              </div>
            </div>

            <div className="contact__field">
              <label className="contact__label">Subject</label>
              <input
                type="text"
                name="subject"
                placeholder="What's this about?"
                value={values.subject}
                onChange={handleChange}
                className={`contact__input ${errors.subject ? 'contact__input--error' : ''}`}
                required
              />
              {errors.subject && <span className="contact__error">{errors.subject}</span>}
            </div>

            <div className="contact__field">
              <label className="contact__label">Message</label>
              <textarea
                name="message"
                placeholder="Tell me about the role or project..."
                rows={6}
                value={values.message}
                onChange={handleChange}
                className={`contact__input contact__textarea ${errors.message ? 'contact__input--error' : ''}`}
                required
              />
              {errors.message && <span className="contact__error">{errors.message}</span>}
            </div>

            <motion.button
              type="submit"
              className="btn btn--primary contact__submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 18 }}
            >
              {isSubmitting ? 'Sending…' : <><span>Send Message</span><IconArrowRight size={16} /></>}
            </motion.button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};
